import React from "react";
import { Card, Col, Form, Row, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { IChangePassword } from "../../../shared/interfaces/user/IUser";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { UserService } from "../service";
import { useNavigate, useParams } from "react-router-dom";
import { handleError } from "../../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";

const schema = yup.object({
  password: yup
    .string()
    .required("validation.required")
    .min(8, "validation.passMinLength"),
  confirmationPassword: yup
    .string()
    .required("validation.required")
    .oneOf([yup.ref("password")], "validation.passNotMatch"),
});
const ChangePassword = () => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<IChangePassword>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: IChangePassword) => {
    await UserService.changePassword(data, Number(userId))
      .then((response) => {
        if (response) {
          enqueueSnackbar(
            t("toasts.successfullyUpdated", {
              name: t("general.password"),
            }),
            {
              variant: "success",
            },
          );
          navigate(-1);
        }
      })
      .catch(handleError);
  };

  return (
    <React.Fragment>
      <Tab.Pane
        eventKey="ChangePassword"
        className="fade"
        id="user-set-passwort"
        role="tabpanel"
        aria-labelledby="user-set-passwort-tab"
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <Card.Header>
              <h5>{t("general.changePassword")}</h5>
            </Card.Header>

            <Card.Body>
              <ul className="list-group list-group-flush">
                <li className="list-group-item px-0">
                  <Row className="mb-0">
                    <label className="col-form-label col-md-4 col-sm-12 text-md-end">
                      {t("general.newPassword")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <Col md={8} sm={12}>
                      <Form.Control
                        type="password"
                        isInvalid={!!errors.password}
                        {...register("password")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password?.message &&
                          t(errors.password.message as string)}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                </li>

                <li className="list-group-item pb-0 px-0">
                  <Row className="mb-0">
                    <label className="col-form-label col-md-4 col-sm-12 text-md-end">
                      {t("general.confirmPassword")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <Col md={8} sm={12}>
                      <Form.Control
                        type="password"
                        isInvalid={!!errors.confirmationPassword}
                        {...register("confirmationPassword")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmationPassword?.message &&
                          t(errors.confirmationPassword.message as string)}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                </li>
              </ul>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body className="text-end">
              <button
                type="button"
                className="btn btn-outline-secondary me-2"
                onClick={() => navigate(-1)}
              >
                {t("button.cancel")}
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {t("general.changePassword")}
              </button>
            </Card.Body>
          </Card>
        </Form>
      </Tab.Pane>
    </React.Fragment>
  );
};

export default ChangePassword;
